import React, { useState } from "react";
import { debounce } from "lodash";
import Select, { StylesConfig } from "react-select";
import { ClassNameMap } from "@mui/material";
import ApiService from "../../../service/ApiService";
import {
  LightPrimaryColor,
  PrimaryColor,
  White,
} from "../../../shared/styles/colors";
import Pathnames from "../../../model/enum/Pathnames";

const styles: StylesConfig<any, true> = {
  container: (base) => ({
    ...base,
    width: "100% !important",
  }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? PrimaryColor : base.borderColor,
    boxShadow: state.isFocused ? LightPrimaryColor : base.boxShadow,
    "&:hover": {
      borderColor: PrimaryColor,
    },
    "&:selected": {
      borderColor: PrimaryColor,
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? LightPrimaryColor
      : state.isSelected
      ? PrimaryColor
      : base.backgroundColor,
    color: state.isFocused || state.isSelected ? White : base.color,
  }),
  menu: (base) => ({
    ...base,
  }),
};

enum Types {
  CLIENT = "client",
  APPOINTMENT = "appointment",
  USER = "user",
  PROJECT = "project",
  SERVICE = "service",
  ESTIMATE = "estimate",
  ROLE = "role",
}

interface SearchResult {
  id: number;
  firstName: string;
  lastName: string;
  type?: Types;
}

interface Props {
  classes?: ClassNameMap;
}

interface Item {
  value: number;
  label: string;
  type?: string; // Optional type for additional context
}

const SearchSelect: React.FC<Props> = ({ classes }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);

  // Debounced search function
  const debouncedSearch = debounce(async (query: string) => {
    if (query.trim().length < 2) return;

    setIsLoading(true);
    try {
      const response: any = await ApiService.get(`search?q=${query}`);
      setSearchResults(response);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, 300);

  // Handle input changes
  const handleInputChange = (inputValue: string) => {
    setSearchQuery(inputValue);
    debouncedSearch(inputValue);
  };

  // Handle selection
  const handleChange = (selectedOption: any) => {
    if (!selectedOption) {
      setSelectedItem(null);
      return;
    } else {
      setSearchQuery(selectedOption.label);
      setSelectedItem(selectedOption);
      navigateToPath(selectedOption);
    }
  };

  const navigateToPath = (item: Item) => {
    // Navigate based on the selected item's type
    if (!item || !item.type || !item.value) {
      console.warn("Selected item is missing type or value:", item);
      return;
    }

    switch (item.type) {
      case Types.CLIENT:
        window.location.href = `${Pathnames.CLIENTS_LIST}?page=1&rowsPerPage=10&clientId=${item.value}`;
        break;
      case Types.APPOINTMENT:
        window.location.href = `${Pathnames.APPOINTMENTS_LIST}?page=1&rowsPerPage=10&appointmentId=${item.value}`;
        break;
      case Types.USER:
        window.location.href = `${Pathnames.USERS_LIST}?page=1&rowsPerPage=10&userId=${item.value}`;
        break;
      case Types.PROJECT:
        window.location.href = `${Pathnames.PROJECTS_LIST}?page=1&rowsPerPage=10&projectId=${item.value}`;
        break;
      case Types.SERVICE:
        window.location.href = `${Pathnames.SERVICES_LIST}?page=1&rowsPerPage=10&serviceId=${item.value}`;
        break;
      case Types.ESTIMATE:
        window.location.href = `${Pathnames.ESTIMATES_LIST}?page=1&rowsPerPage=10&estimateId=${item.value}`;
        break;
      case Types.ROLE:
        window.location.href = `${Pathnames.ROLES_LIST}?page=1&rowsPerPage=10&roleId=${item.value}`;
        break;
      default:
        console.warn("Unknown type:", item.type);
        break;
    }
  };

  // Convert searchResults to options safely
  const options = Array.isArray(searchResults)
    ? searchResults.map((result) =>
        result.type === Types.CLIENT
          ? {
              value: result.id,
              label: `${result.firstName} ${result.lastName}`,
              type: result.type,
            }
          : {
              value: result.id,
              label: result.firstName || result.lastName || `Item ${result.id}`,
              type: result.type,
            }
      )
    : [];

  return (
    <div
      className={classes?.inputGroup}
      style={{
        width: "100%",
        maxWidth: "600px",
      }}
    >
      <style>
        {`
        @media (max-width: 600px) {
        .${classes?.inputGroup || ""} {
          margin-left: -1rem !important;
          max-width: 100% !important;
        }
        }
      `}
      </style>
      <Select
        styles={styles}
        isClearable
        isSearchable
        isLoading={isLoading}
        inputValue={searchQuery}
        placeholder="Search..."
        onInputChange={handleInputChange}
        onChange={handleChange}
        value={
          selectedItem
            ? {
                value: selectedItem.value,
                label: `${selectedItem.label}`,
                type: selectedItem.type,
              }
            : null
        }
        options={options}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? "Type at least 2 characters..."
            : "No results found"
        }
      />
    </div>
  );
};

export default SearchSelect;
